// components/layout/TopHeader.tsx
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppHeader from '@/components/layout/AppHeader';
import { usePageHeaderContent } from '@/contexts/PageHeaderContext';

interface PageConfig {
    title: string;
    subtitle?: string;
}

interface TopHeaderProps {
    onMenuClick: () => void;
    sidebarCollapsed: boolean;
}

// Routes whose page injects its own header content into the strip via
// <PageHeader> (e.g. chat's Link-branding + Tillbaka button). For these
// we keep the AppHeader chrome but render no path-based title — the
// page's injected content (or, if none, nothing) takes the lead.
// NOTE: every other dashboard page now puts its title in this top strip
// via getPageConfig (the page body carries only content + action
// buttons, no duplicate heading) — keeping headings consistent app-wide.
const SELF_HEADERED_ROUTES = [
    '/dashboard/chat',
];

const TopHeader: React.FC<TopHeaderProps> = ({ onMenuClick }) => {
    const { user } = useAuth();
    const location = useLocation();

    // Get greeting based on time
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'God morgon';
        if (hour < 18) return 'God eftermiddag';
        return 'God kväll';
    };

    // Get user first name
    const getFirstName = () => {
        if (user?.first_name) return user.first_name;
        if (user?.email) return user.email.split('@')[0];
        return 'där';
    };

    // Get page configuration based on route and user role
    const getPageConfig = (): PageConfig => {
        const path = location.pathname;
        const role = user?.role;

        // Dashboard index — the guided Home. The page body carries its own warm
        // greeting, so the strip just labels the section (no duplicate greeting).
        if (path === '/dashboard') {
            if (role === 'admin') {
                return { title: 'Hem', subtitle: 'Det som väntar på dig' };
            }
            return { title: 'Hem' };
        }

        // Candidate section pages — give the top header the REAL heading so each page
        // doesn't render its own duplicate title below the greeting strip.
        if (path.includes('/my-applications')) {
            return { title: 'Mina Ansökningar', subtitle: 'Följ status på dina inskickade ansökningar' };
        }
        if (path.includes('/my-invitations')) {
            return { title: 'Mina Inbjudningar', subtitle: 'Inbjudningar du har fått från arbetsgivare' };
        }
        if (path.includes('/moduler') || path.includes('/my-onboarding')) {
            return { title: 'Moduler', subtitle: 'Din onboarding och moduler på plattformen' };
        }
        if (path.includes('/my-workplace')) {
            return { title: 'Min Arbetsplats', subtitle: 'Din organisation, enhet och kollegor' };
        }
        if (path.includes('/jobs')) {
            return { title: 'Lediga Tjänster', subtitle: 'Bläddra bland tillgängliga tjänster och skicka din ansökan' };
        }

        // Sub-route pages (shared paths with role-specific titles)
        if (path.includes('/users')) {
            return { title: 'Användare', subtitle: 'Hantera användare och behörigheter' };
        }
        if (path.includes('/organisations/new')) {
            return { title: 'Ny organisation', subtitle: 'Organisation, första enheten och arbetsgivaren' };
        }
        if (path.includes('/organisations')) {
            return { title: 'Organisationer', subtitle: 'Organisationer, enheter och arbetsgivare' };
        }
        if (path.includes('/contacts')) {
            return { title: 'Kontaktförfrågningar', subtitle: 'Inkomna förfrågningar från landningssidan' };
        }
        if (path.includes('/needs')) {
            return { title: 'Rekrytering', subtitle: 'Skapa annonser, hantera ansökningar och anställ' };
        }
        if (path.includes('/employees')) {
            return { title: 'Medarbetare', subtitle: 'Anställda och inbjudna i din organisation' };
        }
        if (path.includes('/invitations')) {
            return { title: 'Inbjudningar', subtitle: 'Bjud in anställda till plattformen' };
        }
        if (path.includes('/courses')) {
            return { title: 'Moduler', subtitle: 'Dina återanvändbara moduler — för program och som krav vid rekrytering' };
        }
        if (path.includes('/library')) {
            return { title: 'Bibliotek', subtitle: 'Källor du lagt till — Link kan använda dem när du skapar moduler' };
        }
        if (path.includes('/matching')) {
            return { title: 'Matchning', subtitle: 'Se matchade kandidater för dina annonser' };
        }
        if (path.includes('/profile')) {
            if (role === 'employer') {
                return { title: 'Min Profil', subtitle: 'Hantera din arbetsgivarprofil' };
            }
            return { title: 'Min Profil', subtitle: 'Det här ser arbetsgivare när du söker jobb' };
        }
        if (path.includes('/modules')) {
            return { title: 'Moduler', subtitle: 'Din onboarding och moduler på plattformen' };
        }
        if (path.includes('/tasks')) {
            return { title: 'Uppgifter', subtitle: 'Se och hantera dina uppgifter' };
        }
        if (path.includes('/results')) {
            return { title: 'Resultat', subtitle: 'Se dina prestationer och framsteg' };
        }
        if (path.includes('/community')) {
            return { title: 'Community', subtitle: 'Anslut med andra kandidater' };
        }
        if (path.includes('/reports')) {
            if (role === 'admin') {
                return { title: 'Rapporter', subtitle: 'Analysera plattformens prestanda' };
            }
            return { title: 'Statistik', subtitle: 'Analysera dina rekryteringar' };
        }
        if (path.includes('/support')) {
            if (role === 'admin') {
                return { title: 'Support', subtitle: 'Hjälp användare med deras frågor' };
            }
            return { title: 'Support', subtitle: 'Få hjälp och svar på dina frågor' };
        }
        if (path.includes('/messages')) {
            return { title: 'Meddelanden', subtitle: 'Kommunicera med kandidater' };
        }
        if (path.includes('/interviews')) {
            return { title: 'Intervjuer', subtitle: 'Hantera dina intervjuer' };
        }
        if (path.includes('/onboarding')) {
            if (role === 'admin') {
                return { title: 'Onboarding', subtitle: 'Lägg till nya kunder i systemet' };
            }
            return { title: 'Onboarding', subtitle: 'Kom igång med plattformen' };
        }

        // Default fallback
        return {
            title: 'Välkommen tillbaka!',
            subtitle: `${getGreeting()}, ${getFirstName()}.`
        };
    };

    // A page may inject custom header content via <PageHeader> (see
    // PageHeaderContext). When present, render it as the strip's
    // center content — overrides the path-based title. This is what
    // lets the chat page render its Link-branding + Tillbaka button
    // in the same row as the user dropdown instead of stacking a
    // second header below.
    const customContent = usePageHeaderContent();
    if (customContent) {
        return (
            <AppHeader onMenuClick={onMenuClick} hideLogo className="z-30">
                {customContent}
            </AppHeader>
        );
    }

    // Pages explicitly listed as self-headered render no title at all —
    // they're rendering their own visual header inside the page body
    // (a polish pass still open per page). The strip stays for the
    // user dropdown / hamburger.
    const path = location.pathname;
    const isSelfHeadered = SELF_HEADERED_ROUTES.some((r) => path === r || path.startsWith(r + '/'));
    if (isSelfHeadered) {
        return <AppHeader onMenuClick={onMenuClick} hideLogo className="z-30" />;
    }

    const pageConfig = getPageConfig();

    return (
        <AppHeader onMenuClick={onMenuClick} hideLogo className="z-30">
            <div className="min-w-0">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                    {pageConfig.title}
                </h1>
                {pageConfig.subtitle && (
                    <p className="hidden sm:block text-sm text-gray-500 truncate">
                        {pageConfig.subtitle}
                    </p>
                )}
            </div>
        </AppHeader>
    );
};

export default TopHeader;
